import { AxiosResponse, CanceledError, isAxiosError } from "axios"
import { CustomHTTPAxiosConfig } from "src/boot/axios"
import { useApiAbortStore } from "src/stores/apiAbortStore"
import { useApiCallStore } from "src/stores/apiCallStore"
import { Log } from "src/util/classes/log"
import {
  Attachment,
  AttachmentContext,
  AttachmentUpload,
} from "src/util/types/dataModels/attachment"
import { BatchErrors, ObjectList } from "src/util/types/dataModels/genericObject"
import { HttpAbortConfig, HttpConfig, HttpRequestType } from "src/util/types/dataModels/http"
import { UserPermissionAccess, UserPermissionType } from "src/util/types/dataModels/user"

// Concatenate a given url with different parameters separate by a "?"
export function concatUrl(
  url: string,
  args: Record<string, string | number | Array<number | string> | boolean | undefined | null>
) {
  const params = new URLSearchParams()

  for (const [key, value] of Object.entries(args)) {
    if (value !== undefined) {
      if (Array.isArray(value)) {
        const arrayString = value.join(",")
        params.append(key, arrayString)
      } else {
        params.append(key, String(value))
      }
    }
  }

  const queryString = params.toString()
  return queryString ? url + "?" + queryString : url
}

export function getNewAbortHandler(requestType: HttpRequestType, url?: string) {
  const apiAbortStore = useApiAbortStore()
  return apiAbortStore.createController(requestType, url)
}

export function getCustomConfig(
  abortConfig: HttpAbortConfig,
  permissionType?: UserPermissionType,
  accessLevel?: UserPermissionAccess
): CustomHTTPAxiosConfig {
  return {
    sfxAbortId: abortConfig.id,
    multiRequest: Boolean(abortConfig.multiRequest),
    permissionType,
    accessLevel,
  }
}

export async function getAllEntitiesSequential<T>(
  httpFunction: (config: HttpConfig, ...args: unknown[]) => Promise<ObjectList<T>>,
  options: EntityConcurrentOptions,
  config: HttpConfig,
  list: T[] = [],
  page = 1,
  page_size = 250
): Promise<T[]> {
  // Somehow we fucked up and passed bad inputs
  if (page <= 0 || page_size < 0) {
    Log.error({
      message: "Received negative values for page or page_size",
      data: { page, page_size },
    })

    if (config.abortHandler) {
      const apiAbortStore = useApiAbortStore()
      apiAbortStore.removeController(config.abortHandler.id)
    }
    return list
  }

  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", options.callUrl)
  abortHandler.multiRequest = true

  const data = await httpFunction({
    abortHandler,
    mainParams: {
      page,
      page_size,
      updated_dt__gte: options.lastCall,
    },
  })
  list.push(...data.results)

  // No more items
  if (data.count <= page * page_size) {
    const apiAbortStore = useApiAbortStore()
    apiAbortStore.removeController(abortHandler.id)
    return list
  }

  return await getAllEntitiesSequential(httpFunction, options, config, list, ++page, page_size)
}

export async function getAllEntitiesConcurrent<T>(
  httpFunction: (config: HttpConfig, ...args: unknown[]) => Promise<ObjectList<T>>,
  options: EntityConcurrentOptions,
  config?: HttpConfig,
  page_size = 250
): Promise<T[]> {
  let list: T[] = []
  const apiAbortStore = useApiAbortStore()
  const apiCallStore = useApiCallStore()
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", options.callUrl)
  abortHandler.multiRequest = true

  const initialRequest = await httpFunction({
    mainParams: { page_size, updated_dt__gte: options.lastCall },
    abortHandler,
  })

  list.push(...initialRequest.results)

  if (initialRequest.count > page_size) {
    const maxRequests = Math.ceil(initialRequest.count / page_size)
    const requests = []

    // "i" starts at 1 so that we skip the initial request
    for (let i = 1; i < maxRequests; i++) {
      const page = i + 1 // Pages begin from 1, so they are off by 1
      requests.push(
        httpFunction({
          mainParams: { page, page_size, updated_dt__gte: options.lastCall },
          abortHandler,
        })
      )
    }

    const rawRemainingData = await Promise.allSettled(requests)
    const remainingData = rawRemainingData.flatMap((data) =>
      data.status === "fulfilled" ? data.value.results : []
    )

    list = list.concat(remainingData)
  }

  // If not aborted, then we successfully fetched the data, so update last call time
  if (!abortHandler.controller.signal.aborted) {
    apiCallStore.updateLastApiCallTimeConcurrent(options.callUrl)
  }

  if (!abortHandler.batchRequest) {
    // Need to manually remove controller as if we do it here, it would stop counting for other request in the batch
    apiAbortStore.removeController(abortHandler.id)
  }

  return list
}

export async function getAllEntitiesForIdConcurrent<T>(
  httpFunction: (config: HttpConfig, id: string) => Promise<ObjectList<T>>,
  options: EntityConcurrentOptions,
  id: string,
  config?: HttpConfig,
  page_size = 250
): Promise<T[]> {
  let list: T[] = []
  const apiAbortStore = useApiAbortStore()
  const apiCallStore = useApiCallStore()
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", options.callUrl)
  abortHandler.multiRequest = true

  const initialRequest = await httpFunction(
    {
      mainParams: { page_size, updated_dt__gte: options.lastCall },
      abortHandler,
    },
    id
  )

  list.push(...initialRequest.results)

  if (initialRequest.count > page_size) {
    const maxRequests = Math.ceil(initialRequest.count / page_size)
    const requests = []

    // "i" starts at 1 so that we skip the initial request
    for (let i = 1; i < maxRequests; i++) {
      const page = i + 1 // Pages begin from 1, so they are off by 1
      requests.push(
        httpFunction(
          {
            mainParams: { page, page_size, updated_dt__gte: options.lastCall },
            abortHandler,
          },
          id
        )
      )
    }

    const rawRemainingData = await Promise.allSettled(requests)
    const remainingData = rawRemainingData.flatMap((data) =>
      data.status === "fulfilled" ? data.value.results : []
    )

    list = list.concat(remainingData)
  }

  // If not aborted, then we successfully fetched the data, so update last call time
  if (!abortHandler.controller.signal.aborted) {
    apiCallStore.updateLastApiCallTimeConcurrent(options.callUrl)
  }

  if (!abortHandler.batchRequest) {
    // Need to manually remove controller as if we do it here, it would stop counting for other request in the batch
    apiAbortStore.removeController(abortHandler.id)
  }

  return list
}

export type EntityConcurrentOptions = {
  lastCall: string | undefined
  callUrl: string
}

/**
 * Concurrent fetch method specifically for fetching the attachments for various entities.
 * We use it since the backend doesn't want to bother making endpoints for getting them in an easy way.
 * Boo!
 */
export async function getAllAttachmentForEntity(
  httpFunction: (config: HttpConfig, entityId: string) => Promise<ObjectList<Attachment>>,
  options: EntityConcurrentOptions,
  entityId: string,
  page_size = 250
): Promise<Attachment[]> {
  let list: Attachment[] = []
  const apiAbortStore = useApiAbortStore()
  const apiCallStore = useApiCallStore()
  const abortHandler = getNewAbortHandler("GET", options.callUrl)
  abortHandler.multiRequest = true

  const initialRequest = await httpFunction(
    {
      mainParams: { page_size, updated_dt__gte: options.lastCall },
      abortHandler,
    },
    entityId
  )

  list.push(...initialRequest.results)

  if (initialRequest.count > page_size) {
    const maxRequests = Math.ceil(initialRequest.count / page_size)
    const requests = []

    // "i" starts at 1 so that we skip the initial request
    for (let i = 1; i < maxRequests; i++) {
      const page = i + 1 // Pages begin from 1, so they are off by 1
      requests.push(
        httpFunction(
          {
            mainParams: { page, page_size, updated_dt__gte: options.lastCall },
            abortHandler,
          },
          entityId
        )
      )
    }

    const rawRemainingData = await Promise.allSettled(requests)
    const remainingData = rawRemainingData.flatMap((data) =>
      data.status === "fulfilled" ? data.value.results : []
    )

    list = list.concat(remainingData)
  }

  // If not aborted, then we successfully fetched the data, so update last call time
  if (!abortHandler.controller.signal.aborted) {
    apiCallStore.updateLastApiCallTimeConcurrent(options.callUrl)
  }

  apiAbortStore.removeController(abortHandler.id)

  return list
}

/**
 * If no form data is provided, it will create on its own.
 * If you provide form data, there is no need to reassign it.
 *
 * @param attachment
 * @param formData
 */
export function appendAttachmentToFormData(
  attachment: AttachmentUpload,
  formData = new FormData()
): FormData {
  const name = attachment.name || attachment.data.name
  formData.append(name, attachment.data)

  if (attachment.description) {
    formData.append("description", attachment.description)
  }

  return formData
}

export function handleAttachmentBatchResponses(
  response: AxiosResponse<ObjectList<Attachment> | ObjectList<BatchErrors | Attachment>>,
  context: AttachmentContext
): ObjectList<Attachment> | ObjectList<BatchErrors | Attachment> {
  response.data.results = response.data.results.map((i) => {
    if ("id" in i) i.context = [context]
    return i
  })

  return response.data
}

export function isIgnoredError(error: unknown) {
  // We don't care about canceled errors since they are handled
  if (error instanceof CanceledError) return true

  // We don't care about forbidden errors since they are handled
  return isAxiosError(error) && (error.status === 403 || error.response?.status === 403)
}
